import "@/App.css";
import { Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AuthCallback from "@/components/AuthCallback";
import MobileTabBar from "@/components/MobileTabBar";

// Landing is the 404 fallback — keep eagerly loaded so the catch-all route
// never has to wait on a chunk.
import Landing from "@/pages/Landing";

// Always-on global widgets (kept eager — they render on every page).
import FeedbackWidget from "@/components/FeedbackWidget";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import ChatbotWidget from "@/components/ChatbotWidget";
import UsageTracker from "@/components/UsageTracker";
import RouteFallback from "@/components/RouteFallback";

// Lazy-loaded route groups — each chunk is fetched only when its routes
// are visited. Cuts initial JS payload ~40% (~150 eager imports → 4 groups).
import { publicRoutes } from "@/routes/PublicRoutes";
import { indiaRoutes } from "@/routes/IndiaRoutes";
import { toolsRoutes } from "@/routes/ToolsRoutes";
import { adminRoutes } from "@/routes/AdminRoutes";

import useDynamicSEOKeywords from "@/lib/useDynamicSEOKeywords";
import useAnalytics from "@/lib/useAnalytics";
import useOgVersionUpdater from "@/lib/useOgVersionUpdater";

// Module-scope: avoids creating a new object on every render.
const TOAST_STYLE = {
  background: "#13161E",
  border: "1px solid #2A2F3D",
  color: "#F0F2F8",
};
const TOAST_OPTIONS = { style: TOAST_STYLE };

function AppRoutes() {
  const location = useLocation();
  useDynamicSEOKeywords();
  useAnalytics();
  useOgVersionUpdater();

  // Intercept Emergent OAuth fragment synchronously (per playbook race-condition fix)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {publicRoutes}
          {indiaRoutes}
          {toolsRoutes}
          {adminRoutes}
          {/* Catch-all — Landing is eagerly imported above so no chunk fetch. */}
          <Route path="*" element={<Landing />} />
        </Routes>
      </Suspense>
      {!location.pathname.startsWith("/widgets/") && (
        <>
          <MobileTabBar />
          <FeedbackWidget />
          <WhatsAppFloat />
          <ChatbotWidget />
          <UsageTracker />
        </>
      )}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster theme="dark" position="bottom-right" toastOptions={TOAST_OPTIONS} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
